import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, AlertCircle, Shield, FileCheck } from 'lucide-react';
import { Button } from './ui/button';

const MaintenanceContracts = () => {
  const { t } = useLanguage();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="maintenance" className="py-24 bg-[#1e293b]">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="heading-2 text-[#60a5fa] mb-4">{t.maintenance.title}</h2>
          <p className="body-large text-[#94a3b8] max-w-3xl mx-auto mb-6">
            {t.maintenance.subtitle}
          </p>
          <p className="body-medium text-[#94a3b8] max-w-4xl mx-auto leading-relaxed">
            {t.maintenance.description}
          </p>
        </div>

        {/* Contract Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {t.maintenance.contracts.map((contract, index) => (
            <div
              key={index}
              className={`bg-[#0f172a] border-2 ${
                contract.popular ? 'border-[#60a5fa]' : 'border-[#1e40af]'
              } p-8 rounded-lg relative hover:border-[#60a5fa] transition-all duration-300`}
            >
              {contract.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#60a5fa] text-[#0f172a] px-4 py-1 rounded-full text-sm font-semibold">
                    {t.language === 'en' ? 'MOST POPULAR' : 'MEEST POPULAIR'}
                  </span>
                </div>
              )}
              {contract.savings && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-[#10b981] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {contract.savings}
                  </span>
                </div>
              )}
              
              <h3 className="heading-4 text-[#60a5fa] mb-2">{contract.title}</h3>
              <div className="mb-6">
                <span className="heading-1 text-[#60a5fa]">{contract.price}</span>
                <span className="body-small text-[#94a3b8] ml-2">{contract.period}</span>
                <p className="caption text-[#94a3b8] mt-1">
                  {t.language === 'en' ? 'incl. VAT' : 'incl. BTW'} • {contract.duration}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {contract.services.map((service, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-[#60a5fa] flex-shrink-0 mt-0.5" />
                    <span className="body-small text-[#94a3b8]">{service}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => scrollToSection('quote')}
                className={`w-full ${
                  contract.popular ? 'btn-primary' : 'btn-secondary'
                }`}
              >
                {t.language === 'en' ? 'Get Started' : 'Begin Nu'}
              </Button>
            </div>
          ))}
        </div>

        {/* One-Off Inspection */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-[#0f172a] border border-[#1e40af] p-8 rounded-lg">
            <div className="flex items-center space-x-3 mb-4">
              <FileCheck className="w-8 h-8 text-[#60a5fa]" />
              <div>
                <h3 className="heading-4 text-[#60a5fa]">{t.maintenance.oneOff.title}</h3>
                <p className="body-small text-[#94a3b8]">{t.maintenance.oneOff.description}</p>
              </div>
            </div>
            
            <div className="mb-6">
              <span className="heading-2 text-[#60a5fa]">{t.maintenance.oneOff.price}</span>
              <span className="body-small text-[#94a3b8] ml-2">
                {t.language === 'en' ? 'incl. VAT' : 'incl. BTW'}
              </span>
            </div>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {t.maintenance.oneOff.services.map((service, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-[#60a5fa] flex-shrink-0 mt-0.5" />
                  <span className="body-small text-[#94a3b8]">{service}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => scrollToSection('quote')}
              className="btn-secondary w-full"
            >
              {t.language === 'en' ? 'Book Inspection' : 'Inspectie Boeken'}
            </Button>
          </div>
        </div>

        {/* Why Important Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-[#0f172a] border border-[#1e40af] p-8 rounded-lg">
            <div className="flex items-center space-x-3 mb-6">
              <Shield className="w-8 h-8 text-[#60a5fa]" />
              <h3 className="heading-4 text-[#60a5fa]">{t.maintenance.whyImportant.title}</h3>
            </div>
            <ul className="space-y-3">
              {t.maintenance.whyImportant.reasons.map((reason, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-[#60a5fa] flex-shrink-0 mt-0.5" />
                  <span className="body-small text-[#94a3b8]">{reason}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#0f172a] border border-[#1e40af] p-8 rounded-lg flex items-center">
            <div>
              <div className="flex items-start space-x-3 mb-4">
                <AlertCircle className="w-8 h-8 text-[#f59e0b] flex-shrink-0" />
                <div>
                  <h4 className="heading-5 text-[#60a5fa] mb-3">
                    {t.language === 'en' ? 'Cancellation Policy' : 'Annuleringsbeleid'}
                  </h4>
                  <p className="body-small text-[#94a3b8] leading-relaxed">
                    {t.maintenance.cancellation}
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#1e40af]">
                <p className="body-small text-[#94a3b8]">
                  {t.language === 'en' 
                    ? 'All prices include VAT. Contracts renew automatically unless cancelled within the specified period.' 
                    : 'Alle prijzen zijn inclusief BTW. Contracten worden automatisch verlengd tenzij geannuleerd binnen de opgegeven periode.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="body-medium text-[#94a3b8] mb-6">
            {t.language === 'en' 
              ? 'Ready to protect your roof investment?' 
              : 'Klaar om uw dakinvestering te beschermen?'}
          </p>
          <Button
            onClick={() => scrollToSection('quote')}
            className="btn-primary"
          >
            {t.language === 'en' ? 'Request Quote' : 'Offerte Aanvragen'}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MaintenanceContracts;
